/**
 * Per-user ordering of the left-hand navigation menu.
 *
 * Two things are orderable — the SECTIONS (LAB, BILLING / RCM, …) and the pages
 * inside each section — and any page can be hidden from the menu. All of it is
 * saved per user on the server, so it follows the person to any browser they
 * sign in from; localStorage is only an instant-paint cache so the menu doesn't
 * visibly re-sort on every reload.
 *
 * A saved layout is a preference, never a permission. It can only rearrange or
 * declutter what the sidebar was already going to show: a page the user has no
 * rights to stays invisible however it is ordered, and a page added by a later
 * release still appears (at the end of its section) without being saved first.
 *
 * Hiding, in particular, is not a lock. A hidden page still answers at its
 * URL, still turns up in the sidebar's search box, and still shows under
 * FAVORITES if it is starred — see applyHidden().
 */
import { TOP_GROUP_KEY, groupKey } from './navMenu';

const API_BASE = process.env.REACT_APP_API_URL || (window.location.protocol === 'https:'
  ? `${window.location.protocol}//${window.location.host}`
  : `http://${window.location.hostname}:8000`);

export const EMPTY_LAYOUT = { group_order: [], item_order: {}, hidden_paths: [] };

/** Fired after a layout is saved or reset, so an open sidebar re-reads it
 *  without a page reload — the editor in My Account and the sidebar are on
 *  screen at the same time. */
export const NAV_LAYOUT_EVENT = 'lis:nav-layout-changed';

const cacheKey = (userId) => (userId ? `navLayout:u${userId}` : 'navLayout');

const normalize = (raw) => {
  const groupOrder = Array.isArray(raw?.group_order)
    ? raw.group_order.filter(k => typeof k === 'string')
    : [];
  const itemOrder = {};
  const src = raw?.item_order;
  if (src && typeof src === 'object' && !Array.isArray(src)) {
    Object.keys(src).forEach(k => {
      if (Array.isArray(src[k])) itemOrder[k] = src[k].filter(p => typeof p === 'string');
    });
  }
  const hidden = Array.isArray(raw?.hidden_paths)
    ? raw.hidden_paths.filter(p => typeof p === 'string')
    : [];
  return { group_order: groupOrder, item_order: itemOrder, hidden_paths: hidden };
};

export const isEmptyLayout = (layout) =>
  !layout || (
    (layout.group_order || []).length === 0 &&
    Object.keys(layout.item_order || {}).length === 0 &&
    (layout.hidden_paths || []).length === 0
  );

/** Cached layout, for the first paint before the API answers. */
export function readCachedLayout(userId) {
  try {
    const raw = localStorage.getItem(cacheKey(userId));
    return raw ? normalize(JSON.parse(raw)) : EMPTY_LAYOUT;
  } catch {
    return EMPTY_LAYOUT;
  }
}

const writeCache = (userId, layout) => {
  try { localStorage.setItem(cacheKey(userId), JSON.stringify(layout)); } catch {}
};

/** Authoritative layout from the server. Falls back to the cache on failure
 *  so a network blip never re-sorts somebody's menu under them. */
export async function fetchNavLayout(userId) {
  const token = localStorage.getItem('token');
  if (!token) return readCachedLayout(userId);
  try {
    const r = await fetch(`${API_BASE}/users/me/nav-layout`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return readCachedLayout(userId);
    const layout = normalize(await r.json());
    writeCache(userId, layout);
    return layout;
  } catch {
    return readCachedLayout(userId);
  }
}

/** Persist a layout. Caches and notifies immediately; the request is
 *  best-effort, because the caller has already moved the item on screen. */
export function saveNavLayout(userId, layout) {
  const clean = normalize(layout);
  writeCache(userId, clean);
  window.dispatchEvent(new CustomEvent(NAV_LAYOUT_EVENT, { detail: clean }));
  const token = localStorage.getItem('token');
  if (!token) return Promise.resolve(clean);
  return fetch(`${API_BASE}/users/me/nav-layout`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(clean),
  }).then(() => clean).catch(() => clean);
}

/** Drop the saved layout and go back to the order the product ships. */
export function resetNavLayout(userId) {
  writeCache(userId, EMPTY_LAYOUT);
  window.dispatchEvent(new CustomEvent(NAV_LAYOUT_EVENT, { detail: EMPTY_LAYOUT }));
  const token = localStorage.getItem('token');
  if (!token) return Promise.resolve(EMPTY_LAYOUT);
  return fetch(`${API_BASE}/users/me/nav-layout`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  }).then(() => EMPTY_LAYOUT).catch(() => EMPTY_LAYOUT);
}

/**
 * Reorder `list` to match `order`.
 *
 * Anything named in `order` comes first, in that order. Anything the saved
 * order has never seen — a section or page added by a later release — keeps
 * its default relative position at the end, so a new feature is never hidden
 * by an old preference, it just isn't at the top yet.
 */
const applyOrder = (list, order, identify) => {
  if (!order || order.length === 0) return list;
  const rank = new Map();
  order.forEach((key, i) => { if (!rank.has(key)) rank.set(key, i); });
  const known = [];
  const unknown = [];
  list.forEach(entry => {
    const r = rank.get(identify(entry));
    if (r === undefined) unknown.push(entry);
    else known.push([r, entry]);
  });
  known.sort((a, b) => a[0] - b[0]);
  return [...known.map(k => k[1]), ...unknown];
};

/**
 * Apply a saved layout to menu groups.
 *
 * The FAVORITES group is left where it is: it is pinned to the top and its
 * own order comes from the favorites list, not from here.
 */
export function applyNavLayout(groups, layout) {
  if (!layout || isEmptyLayout(layout)) return groups;
  if ((layout.group_order || []).length === 0
      && Object.keys(layout.item_order || {}).length === 0) return groups;
  const ordered = applyOrder(
    groups.filter(g => !g.isFavorites),
    layout.group_order,
    groupKey,
  );
  const withItems = ordered.map(g => {
    const order = layout.item_order?.[groupKey(g)];
    if (!order || order.length === 0) return g;
    return { ...g, items: applyOrder(g.items, order, it => it.path) };
  });
  const favorites = groups.filter(g => g.isFavorites);
  return [...favorites, ...withItems];
}

/**
 * Remove the pages the user has hidden.
 *
 * Deliberately separate from applyNavLayout: the sidebar orders the menu for
 * every purpose but only hides for the menu itself. When the user searches,
 * the sidebar skips this step, so typing the name of a hidden page finds it —
 * hiding declutters the menu, it does not take the page away.
 *
 * A section left with nothing visible disappears with its pages; hiding all of
 * LAB should not leave an empty LAB heading behind.
 */
export function applyHidden(groups, hiddenPaths) {
  const hidden = new Set(hiddenPaths || []);
  if (hidden.size === 0) return groups;
  return groups
    .map(g => (g.isFavorites ? g : { ...g, items: g.items.filter(it => !hidden.has(it.path)) }))
    .filter(g => g.isFavorites || g.items.length > 0);
}

/** Tag each item with whether it is hidden, without removing it. Used for
 *  search results, so a hidden page can be shown as such rather than looking
 *  like an ordinary menu entry. */
export function markHidden(groups, hiddenPaths) {
  const hidden = new Set(hiddenPaths || []);
  if (hidden.size === 0) return groups;
  return groups.map(g => ({
    ...g,
    items: g.items.map(it => (hidden.has(it.path) ? { ...it, isHidden: true } : it)),
  }));
}

/** The layout that describes `groups` exactly as they currently stand. Used
 *  after a drag: we always persist the whole picture rather than a diff, so
 *  the saved order can't drift out of step with what is on screen.
 *
 *  `groups` must be the list BEFORE hiding is applied, or a hidden page would
 *  drop out of the saved order and resurface at the end of its section the
 *  moment it is unhidden. */
export function layoutFromGroups(groups, hiddenPaths = []) {
  const real = groups.filter(g => !g.isFavorites);
  const item_order = {};
  real.forEach(g => { item_order[groupKey(g)] = g.items.map(it => it.path); });
  return {
    group_order: real.map(groupKey),
    item_order,
    hidden_paths: [...(hiddenPaths || [])],
  };
}

/** Move an entry within an array, from one index to another. */
export function moveInArray(list, fromIdx, toIdx) {
  if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return list;
  if (fromIdx >= list.length || toIdx >= list.length) return list;
  const next = [...list];
  const [moved] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, moved);
  return next;
}

export { TOP_GROUP_KEY, groupKey };
